import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Truck } from 'lucide-react';
import type { ApiResponse } from '@evi/shared';
import { api, getDisplayErrorMessage } from '@/lib/api';
import styles from './SettingsPage.module.css';

/**
 * Warren's PO auto-release limit (21 October 2026): a PO paid in full, with Autel's freight quote and no sales-team
 * conflict, releases itself when its total is under this limit; over it, or with the field empty (the default), a PO
 * approver releases it. Super Admin changes it (Api: routes/settings/frictionless.ts says whether the reader may, and
 * answers 403 to anyone but EVI staff, when this section shows nothing). Every change is audited.
 */
const KEY = ['po-auto-release'];
const dollars = (cents: number | null | undefined) => (typeof cents === 'number' ? String(cents / 100) : '');

export function PoAutoReleaseSection() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, retry: false, staleTime: 60_000,
    queryFn: () => api.get('settings/po-auto-release', { retry: 0 }).json<ApiResponse<{ value: { limit_cents: number | null }; can_edit: boolean }>>().then(r => r.data!) });
  const [text, setText] = useState<string | null>(null), [busy, setBusy] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  if (q.isError) return null;
  const shown = text ?? dollars(q.data?.value.limit_cents);
  const cents = shown.trim() === '' ? null : Math.round(Number(shown) * 100);
  const dirty = cents !== (q.data?.value.limit_cents ?? null);
  async function save() {
    setBusy(true); setError(''); setSaved(false);
    try { await api.put('settings/po-auto-release', { json: { limit_cents: cents } }).json(); await client.invalidateQueries({ queryKey: KEY }); setText(null); setSaved(true); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'The limit was not saved.')); } finally { setBusy(false); }
  }
  return <section className={styles.platformFeeSection}>
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><Truck size={16} /></div>
      <div>
        <h2 className={styles.platformFeeTitle}>PO auto-release limit</h2>
        <p className={styles.platformFeeHint}>A PO releases itself to Autel when its orders are paid in full, Autel&rsquo;s freight quote is on it, nobody on a job&rsquo;s sales
          team drafted or changed it, no handoff or site claim is open, and its total is under this limit. The platform then emails Autel the purchase order.
          Over the limit, or with this empty, a PO approver releases it. Every change is audited.</p>
      </div>
    </div>
    {q.isLoading ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p> : <div className={styles.platformFeeField}>
      <label className={styles.platformFeeLabel} htmlFor="po-auto-release-limit">Limit, in dollars (empty: every PO goes to a PO approver)</label>
      <div className={styles.platformFeeInputRow}>
        <input id="po-auto-release-limit" className={styles.platformFeeInput} type="number" min={1} step={1} placeholder="e.g. 25000" value={shown}
          onChange={e => { setText(e.target.value); setSaved(false); }} disabled={busy || !q.data?.can_edit} />
        {q.data?.can_edit && <button type="button" className={styles.platformFeeBtn} disabled={!dirty || busy || (cents !== null && !(cents > 0))} onClick={() => void save()}>
          {busy ? <><Loader2 size={13} className={styles.spin} /> Saving</> : saved && !dirty ? <><Check size={13} /> Saved</> : <>Save</>}</button>}
      </div>
      {!q.data?.can_edit && <p className={styles.platformFeeHint}>A Super Admin sets the limit.</p>}
      {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
    </div>}
  </section>;
}
